import React, { useLayoutEffect, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

const PRODUCTS = ["HBB", "Baqati", "Hayyak", "Upgrade"];

const SERIES_CONFIG = [
  {
    field: "premiumTarget",
    label: "Premium Target",
    color: "#5B9BD5",
    pctField: "premiumPct",
  },
  {
    field: "premiumSales",
    label: "Premium Sales",
    color: "#7DB8E8",
    pctField: null,
  },
  {
    field: "standardTarget",
    label: "Standard Target",
    color: "#9B8EC4",
    pctField: "standardPct",
  },
  {
    field: "standardSales",
    label: "Standard Sales",
    color: "#F4A460",
    pctField: null,
  },
];

const TOOLTIP_HTML = `
  <div style="font-family:sans-serif; min-width:190px; padding:4px 0;">
    <div style="font-weight:700; font-size:14px; margin-bottom:10px; color:#333;">
      {product}
    </div>
    <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
      <span style="color:#5B9BD5; font-size:13px;">Premium Target:</span>
      <strong style="color:#5B9BD5; font-size:13px; margin-left:12px;">{premiumTarget.formatNumber('#,###')}</strong>
    </div>
    <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
      <span style="color:#7DB8E8; font-size:13px;">Premium Sales:</span>
      <strong style="color:#7DB8E8; font-size:13px; margin-left:12px;">{premiumSales.formatNumber('#,###')}</strong>
    </div>
    <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
      <span style="color:#9B8EC4; font-size:13px;">Standard Target:</span>
      <strong style="color:#9B8EC4; font-size:13px; margin-left:12px;">{standardTarget.formatNumber('#,###')}</strong>
    </div>
    <div style="display:flex; justify-content:space-between;">
      <span style="color:#F4A460; font-size:13px;">Standard Sales:</span>
      <strong style="color:#F4A460; font-size:13px; margin-left:12px;">{standardSales.formatNumber('#,###')}</strong>
    </div>
  </div>
`;

const TargetPerPlanChart = ({ premiumData, standardData }) => {
  const chartRef = useRef(null);

  const chartData = PRODUCTS.map((product) => ({
    product,
    premiumTarget: premiumData?.[product]?.target ?? 0,
    premiumSales: premiumData?.[product]?.sales_count ?? 0,
    standardTarget: standardData?.[product]?.target ?? 0,
    standardSales: standardData?.[product]?.sales_count ?? 0,
    premiumPct: premiumData?.[product]?.achieved_percentage ?? 0,
    standardPct: standardData?.[product]?.achieved_percentage ?? 0,
  }));

  useLayoutEffect(() => {
    if (!premiumData && !standardData) return;

    const chart = am4core.create("targetPerPlanChartDiv", am4charts.XYChart);
    chartRef.current = chart;

    chart.data = chartData;
    chart.paddingRight = 20;

    // ── Category axis (X) ─────────────────────────────────────────
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "product";
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.cellStartLocation = 0.1;
    categoryAxis.renderer.cellEndLocation = 0.9;
    categoryAxis.renderer.labels.template.fontSize = 13;
    categoryAxis.renderer.labels.template.fontWeight = "600";
    // ✅ Safe way to disable axis tooltip
    categoryAxis.cursorTooltipEnabled = false;

    // ── Value axis (Y) ────────────────────────────────────────────
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.labels.template.fontSize = 12;
    valueAxis.numberFormatter.numberFormat = "#,###";
    valueAxis.min = 0;
    valueAxis.extraMax = 0.15;
    // ✅ Safe way to disable axis tooltip
    valueAxis.cursorTooltipEnabled = false;

    // ── Helper: create series ─────────────────────────────────────
    // ── Helper: create series ─────────────────────────────────────
    const createSeries = (
      field,
      name,
      color,
      pctField = null,
      showTooltip = false,
    ) => {
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = field;
      series.dataFields.categoryX = "product";
      series.name = name;
      series.columns.template.fill = am4core.color(color);
      series.columns.template.stroke = am4core.color(color);
      series.columns.template.width = am4core.percent(90);
      series.columns.template.column.cornerRadiusTopLeft = 4;
      series.columns.template.column.cornerRadiusTopRight = 4;

      // ✅ Only the FIRST series carries the tooltip — prevents duplicates
      if (showTooltip) {
        series.columns.template.tooltipHTML = TOOLTIP_HTML;
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color("#ffffff");
        series.tooltip.background.stroke = am4core.color("#e0e0e0");
        series.tooltip.background.strokeWidth = 1;
        series.tooltip.background.cornerRadius = 10;
        series.tooltip.background.filters.clear();
        series.tooltip.autoTextColor = false;
        series.tooltip.label.fill = am4core.color("#333");
        series.tooltip.pointerOrientation = "vertical";
        series.tooltip.dy = -8;
        try {
          const shadow = new am4core.DropShadowFilter();
          shadow.color = am4core.color("#00000022");
          shadow.blur = 8;
          shadow.dx = 0;
          shadow.dy = 2;
          series.tooltip.background.filters.push(shadow);
        } catch (e) {}
      } else {
        // ✅ Disable tooltip on all other series
        series.columns.template.tooltipText = "";
        series.tooltip.disabled = true;
      }

      const hover = series.columns.template.states.create("hover");
      hover.properties.fillOpacity = 0.8;

      // ✅ Only show % label if value is > 0
      if (pctField) {
        const labelBullet = series.bullets.push(new am4charts.LabelBullet());
        labelBullet.label.text = "{" + pctField + "}%";
        labelBullet.label.dy = -10;
        labelBullet.label.fontSize = 11;
        labelBullet.label.fontWeight = "600";
        labelBullet.label.fill = am4core.color("#444");
        labelBullet.label.truncate = false;
        labelBullet.label.hideOversized = false;
        // ✅ Hide label when value is 0
        labelBullet.label.adapter.add("text", (text, target) => {
          const val =
            target.dataItem && target.dataItem.dataContext
              ? target.dataItem.dataContext[pctField]
              : 0;
          return val > 0 ? `${val}%` : "";
        });
      }

      return series;
    };

    // ── Four series — only premiumTarget gets the tooltip ─────────
    SERIES_CONFIG.forEach(({ field, label, color, pctField }, index) => {
      createSeries(field, label, color, pctField, index === 0); // ✅ only first = true
    });

    // ── Legend ────────────────────────────────────────────────────
    chart.legend = new am4charts.Legend();
    chart.legend.position = "bottom";
    chart.legend.labels.template.fontSize = 13;

    // ── Cursor ────────────────────────────────────────────────────
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;
    chart.cursor.behavior = "none";

    return () => {
      chart.dispose();
    };
  }, [premiumData, standardData]);

  if (!premiumData && !standardData) return null;

  return (
    <div style={{ width: "100%" }}>
      <div id="targetPerPlanChartDiv" style={{ width: "100%", height: 440 }} />

      {/* Summary cards */}
      {/* <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 20,
          justifyContent: "center",
        }}
      >
        {chartData.map((row) => (
          <div
            key={row.product}
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: "12px 20px",
              minWidth: 170,
              border: "1px solid #e8e8e8",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 8,
                color: "#333",
              }}
            >
              {row.product}
            </div>
            {SERIES_CONFIG.map(({ field, label, color }) => (
              <div
                key={field}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 4,
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#555" }}>{label}:</span>
                </div>
                <strong style={{ fontSize: 12, color }}>
                  {Number(row[field]).toLocaleString()}
                </strong>
              </div>
            ))}
          </div>
        ))}
      </div> */}
    </div>
  );
};

export default TargetPerPlanChart;
